/**
 * The sidebar's open/closed state, made reachable from inside a route.
 *
 * WHY A CONTEXT AND NOT JUST THE HOOK AGAIN: `useNavSidebar()` is not a pure read. It
 * installs the Escape listener, the close-on-navigate effect and the leaves-drawer-width
 * effect, so calling it a second time in the Studio would install all three twice —
 * two handlers racing to close the same drawer, and an Escape press that is handled,
 * stopped and then handled again. The app shell calls it once and publishes the result;
 * everything else reads.
 *
 * This is not a second store. The state itself lives in uiStore, where the four things
 * that act on it can all reach it; this is only how a component several levels down gets
 * hold of the coordinated view of it without threading five props through the router.
 *
 * The default is absent — no provider means "rendered outside the shell", which happens
 * in unit tests. Reading it returns null rather than throwing, because there is a
 * sensible thing to do without it: do not offer the toggle.
 */
import { createContext, useContext, useEffect } from 'react';
import type { NavSidebar } from '../../hooks';

/** Present when a real provider is above; absent means "rendered outside the shell". */
export const NavSidebarContext = createContext<NavSidebar | null>(null);

/**
 * Lets a route take over responsibility for the drawer's open button.
 *
 * THE PROBLEM THIS SOLVES. On drawer widths the sidebar is off-screen, so something
 * on the page has to be able to summon it or there is no navigation at all — which
 * would be exactly the "control disappearing without a replacement" the brief rules
 * out. So the shell renders one. But the Studio has its own header and its own toggle
 * in it, and two hamburgers on one screen is worse than none.
 *
 * A route that renders its own calls this on mount and the shell stands down. It is a
 * claim rather than a route check because the shell should not have to know which
 * routes those are — the same reason the workspace's layout opt-out is a `:has()`
 * selector rather than a class the shell applies by pathname.
 */
export const NavToggleClaimContext = createContext<(() => () => void) | null>(null);

/**
 * Reads the shell's sidebar state.
 *
 * Returns null outside the shell rather than throwing, because the components that ask
 * are also rendered directly in tests and there is a sensible thing to do without it:
 * hide the toggle.
 */
export function useNavSidebarContext(): NavSidebar | null {
  return useContext(NavSidebarContext);
}

/**
 * Declares that this component renders its own nav toggle, for as long as it is mounted.
 *
 * The claim function is stable, so this runs once on mount and releases on unmount
 * rather than churning every time the drawer opens or closes.
 */
export function useClaimNavToggle(): void {
  const claim = useContext(NavToggleClaimContext);
  useEffect(() => claim?.(), [claim]);
}
